import React from "react";
import { Modal } from "antd";

import { openPlanModal } from "../Subscription/planModal";

// Friendly labels for the six metered actions (matches the backend UsageAction).
const ACTION_LABELS = {
  agent_breakdown_fetch: "insurance verification",
  agent_estimate_fetch: "insurance estimate",
  ftc_breakdown: "breakdown request",
  ftc_estimate: "estimate request",
  ftc_breakdown_urgent: "urgent breakdown request",
  ftc_estimate_urgent: "urgent estimate request",
};

const PURPLE = "#6b43B5";

// Imperatively show the usage-overage modal. Resolves `true` when the user opts
// to proceed (and be billed per request), `false` when they cancel or navigate
// to the upgrade page.
export const showOverageModal = (payload) =>
  new Promise((resolve) => {
    // NB: the payload's period field is destructured as `periodWindow` — naming it
    // `window` shadowed the global, so `goUpgrade` threw instead of navigating.
    const { action, used, limit, overage_price, currency, upgrade_offer, scope, window: periodWindow } = payload || {};
    const isAggregate = scope === "ftc_aggregate";
    // Aggregate FTC caps pool all manual-request types; per-action otherwise.
    const label = isAggregate ? "request" : ACTION_LABELS[action] || "request";
    const periodWord = isAggregate && periodWindow === "daily" ? "today" : "this billing cycle";
    const cur = (currency || "").toUpperCase();
    const price = `${overage_price} ${cur}`.trim();
    // Aggregate upgrade offers carry monthly_cap; per-action ones carry limit.
    const upgradeLimit = upgrade_offer
      ? upgrade_offer.monthly_cap !== undefined
        ? upgrade_offer.monthly_cap
        : upgrade_offer.limit
      : null;
    let settled = false;
    const finish = (v) => {
      if (!settled) {
        settled = true;
        resolve(v);
      }
    };

    const goUpgrade = () => {
      // App uses hash routing. Billing is the real plan/usage/receipts page;
      // /Subscriptions/:uuid is a 2022 mockup of invented invoices that this
      // link used to send paying customers to.
      window.location.hash = "#/billing";
    };

    const modal = Modal.confirm({
      title: "Usage limit reached",
      width: 500,
      icon: null,
      okText: `Proceed & be billed ${price}`,
      cancelText: "Not now",
      okButtonProps: { style: { background: PURPLE, borderColor: PURPLE } },
      onOk: () => finish(true),
      onCancel: () => finish(false),
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            You've used <b>{used}</b> of your <b>{limit}</b> included {label}s {periodWord}.
          </p>
          <p style={{ marginBottom: 8 }}>
            You can keep going — each additional {label} will be billed at <b>{price}</b>{" "}
            per request.
          </p>
          {upgrade_offer ? (
            <p style={{ marginTop: 12, marginBottom: 0 }}>
              Need more? Upgrade to <b>{upgrade_offer.title}</b> ({upgrade_offer.price} {cur},{" "}
              {String(upgradeLimit)} included).{" "}
              <a
                style={{ color: PURPLE, fontWeight: 600 }}
                onClick={() => {
                  modal.destroy();
                  finish(false);
                  goUpgrade();
                }}
              >
                Upgrade now
              </a>
            </p>
          ) : null}
        </div>
      ),
    });
  });

// Wrap a request that may return a 402. `doRequest(confirm)` performs the fetch
// (adding the `X-Confirm-Overage` header when `confirm` is true) and returns
// `{ json, status }`.
//
// Two different 402s arrive here, and they are NOT interchangeable:
//
//   overage_required  — a paying office past its plan's included usage. It can
//                       carry on and be billed per request, so we ask and retry
//                       with confirm=true.
//   trial_exhausted   — a free trial's requests are spent, or
//   plan_required       the office has no plan and no trial at all. There is no
//                       card and nothing to bill, so confirming is meaningless;
//                       the only way forward is a plan: the Choose-a-plan modal
//                       opens and the request is not retried (a purchase reloads
//                       the page once it is paid).
//
// Every request entry point in the app funnels through here, which is why the
// trial wall needs no per-call-site wiring.
export const withOverageRetry = async (doRequest) => {
  let res = await doRequest(false);
  if (!res || res.status !== 402 || !res.json) return res;

  if (res.json.trial_exhausted || res.json.plan_required) {
    openPlanModal({ reason: res.json.trial_exhausted ? "trial_exhausted" : "no_plan" });
    return res;
  }

  if (res.json.overage_required) {
    const proceed = await showOverageModal(res.json);
    if (proceed) res = await doRequest(true);
  }
  return res;
};
